import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Switch,
  Tooltip,
} from "@mui/material";
import React, { useCallback } from "react";
import { observer } from "mobx-react-lite";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { Store } from "../../../models/Swagstore/StoreDetailsModel";
import { useNavigate } from "react-router-dom";
import { SWAGSTORE_STEPS } from "../../../constants/SwagstoreConstants";

interface StoreTableProps {
  stores: Store[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onStatusChange?: (storeId: number | string, isActive: boolean) => void;
  canUpdateStatus?: boolean;
}

const StoreTable = ({
  stores,
  maxHeight,
  page_no,
  limit,
  total_count,
  minHeight = window.innerHeight - 320,
  onPageChange,
  onRowsPerPageChange,
  onStatusChange,
  canUpdateStatus = true,
}: StoreTableProps) => {
  const navigate = useNavigate();

  const resolveSwagstoreBaseUrl = () => {
    const host = window.location.hostname.toLowerCase();
    if (host.includes("localhost")) return "http://localhost:3001";
    if (host.includes("dev")) return "https://dev-swagstore.swageazy.com";
    if (host.includes("stage") || host.includes("staging")) {
      return "https://stage-swagstore.swageazy.com";
    }
    return "https://swagstore.swageazy.com";
  };

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleStoreIdClick = useCallback(
    (storeId: number | string, themeId?: number) => {
      const url = themeId
        ? `/swagstore/create-store?store_id=${storeId}&theme_id=${themeId}&step=${SWAGSTORE_STEPS.STORE_DETAILS}`
        : `/swagstore/create-store?store_id=${storeId}&step=${SWAGSTORE_STEPS.STORE_DETAILS}`;

      navigate(url, {
        replace: false,
        state: { storeId, themeId },
      });
    },
    [navigate],
  );

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell>Store ID</AppTableHeaderCell>
              <AppTableHeaderCell>Store Name</AppTableHeaderCell>
              <AppTableHeaderCell>Customer Name</AppTableHeaderCell>
              <AppTableHeaderCell>Public</AppTableHeaderCell>
              <AppTableHeaderCell>Status</AppTableHeaderCell>
              <AppTableHeaderCell>Created At</AppTableHeaderCell>
              <AppTableHeaderCell>Updated At</AppTableHeaderCell>
              <AppTableHeaderCell>Visit Store</AppTableHeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {stores.map((store, index) => {
              const storeId = store.id ?? store.store_id;
              const rowKey = storeId ? `store-${storeId}` : `store-${index}`;

              return (
                <StyledTableRow key={rowKey}>
                  <TableCell
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      textAlign: "center",
                      fontSize: 14,
                    }}
                  >
                    <Box
                      onClick={() => {
                        const clickedStoreId = store.store_id || store.id;
                        const clickedThemeId = store.theme_id;
                        if (clickedStoreId) {
                          handleStoreIdClick(clickedStoreId, clickedThemeId);
                        }
                      }}
                    >
                      {storeId || "-"}
                    </Box>
                  </TableCell>
                  <AppTableRowCell>{store.store_name || "-"}</AppTableRowCell>
                  <AppTableRowCell>
                    {store.customer_name || "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {(() => {
                      const isPublic = store.is_public as any;
                      if (isPublic === undefined || isPublic === null)
                        return "No";
                      if (
                        isPublic === false ||
                        isPublic === 0 ||
                        isPublic === "0"
                      )
                        return "No";
                      return "Yes";
                    })()}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {onStatusChange && canUpdateStatus ? (
                      <Tooltip
                        title={
                          Boolean(store.is_active)
                            ? "Click to deactivate"
                            : "Click to activate"
                        }
                      >
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const storeIdToUpdate = store.store_id ?? store.id;
                            if (storeIdToUpdate != null) {
                              const newStatus = !Boolean(store.is_active);
                              onStatusChange(storeIdToUpdate, newStatus);
                            }
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            cursor:
                              store.store_id != null || store.id != null
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <Switch
                            checked={Boolean(store.is_active)}
                            size="small"
                            color="primary"
                            disabled={
                              store.store_id == null && store.id == null
                            }
                            sx={{ pointerEvents: "none" }}
                          />
                          <Box component="span" sx={{ fontSize: "0.813rem" }}>
                            {Boolean(store.is_active) ? "Active" : "Inactive"}
                          </Box>
                        </Box>
                      </Tooltip>
                    ) : Boolean(store.is_active) ? (
                      "Active"
                    ) : (
                      "Inactive"
                    )}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {store.created_at
                      ? new Date(store.created_at).toLocaleString()
                      : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    {store.updated_at
                      ? new Date(store.updated_at).toLocaleString()
                      : "-"}
                  </AppTableRowCell>
                  <AppTableRowCell>
                    <a
                      href={`${resolveSwagstoreBaseUrl()}?store_id=${store.store_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visit Store
                    </a>
                  </AppTableRowCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize="0.8rem"
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />
    </>
  );
};

export default observer(StoreTable);
